import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSchedulesStore } from 'pages/Schedule/store/schedules.store';
import { createApolloClient } from 'providers/ApolloProvider';
import {
  AggregatePositionShiftsEnum,
  IncludeConflictsAndWarningsEnum,
  ScheduleByLineSchedulesQueryVariables,
  WithStaffEnum,
} from 'types/graphql';
import selectedScheduleStartDateSelector from 'store/selectors/scheduleSelectors/selectedScheduleStartDateSelector';
import { useSelector } from 'react-redux';
import selectedLineScheduleEndDateSelector from 'store/selectors/scheduleSelectors/selectedLineScheduleEndDateSelector';
import { useScheduleLinesStore } from 'pages/Schedule/store/scheduleLines.store';
import { selectedScheduleFacilityIdSelector } from 'store/selectors/scheduleSelectors/selectedScheduleFacilityIdSelector';
import isPositionShiftsEnableSelector from 'store/selectors/scheduleSelectors/isPositionShiftsEnabledSelector';
import selectedShiftStatusFiltersSelector from 'store/selectors/scheduleSelectors/selectedShiftStatusFiltersSelector';
import selectedJaneStaffTypeFilterSelector from 'store/selectors/scheduleSelectors/selectedJaneStaffTypeFilterSelector';
import selectedEmploymentStatusFilterSelector from 'store/selectors/scheduleSelectors/selectedEmploymentStatusFilterSelector';
import selectedTimeFrameStatusFilterSelector from 'store/selectors/scheduleSelectors/selectedTimeFrameStatusFilterSelector';
import selectedPositionCodeFilterSelector from 'store/selectors/scheduleSelectors/selectedPositionCodeFilterSelector';
import { SessionService } from 'services';
import isShowConflictsOnlyFilterSelectedSelector from 'store/selectors/scheduleSelectors/isShowConflictsOnlyFilterSelectedSelector';
import { selectedLineScheduleRelayIdsSelector } from 'store/selectors/scheduleSelectors/selectedLineScheduleRelayIdsSelector';
import { DateTime } from 'luxon';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';

export const useScheduleRelayIds = (selectedScheduleIds: string[]) => {
  const {
    data: { byLineSchedules },
  } = useSchedulesStore();

  return useMemo(() => {
    if (!byLineSchedules || !selectedScheduleIds?.length) {
      return [];
    }

    return byLineSchedules
      .filter((byLineSchedule) => {
        return selectedScheduleIds.includes(`${byLineSchedule.displayId}`);
      })
      .map(({ relayId }) => relayId);
  }, [byLineSchedules, selectedScheduleIds]);
};

export const useFetchScheduleLinesVariables = () => {
  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const selectedFacilityId = useSelector(selectedScheduleFacilityIdSelector);
  const selectedSchedulesRelayIds = useSelector(selectedLineScheduleRelayIdsSelector);
  const selectedScheduleStartDate = useSelector(selectedScheduleStartDateSelector);
  const selectedScheduleEndDate = useSelector(selectedLineScheduleEndDateSelector);
  const isShowPositionShiftsEnabled = useSelector(isPositionShiftsEnableSelector);
  const selectedJaneStaffTypeFilter = useSelector(selectedJaneStaffTypeFilterSelector);
  const selectedShiftStatusFilters = useSelector(selectedShiftStatusFiltersSelector);
  const selectedEmploymentStatusFilter = useSelector(selectedEmploymentStatusFilterSelector);
  const selectedTimeFrameStatusFilter = useSelector(selectedTimeFrameStatusFilterSelector);
  const selectedPositionCodeFilter = useSelector(selectedPositionCodeFilterSelector);

  let timeZone = currentlySelectedLocation ? currentlySelectedLocation.timeZone : undefined;
  const from = DateTime.fromISO(selectedScheduleStartDate)
    .setZone(timeZone, {
      keepLocalTime: true,
    })
    .startOf('day');
  const to = DateTime.fromISO(selectedScheduleEndDate)
    .setZone(timeZone, { keepLocalTime: true })
    .endOf('day');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let lineFilter: any = {};

  if (selectedJaneStaffTypeFilter?.length) {
    lineFilter.withStaff = {
      types: selectedJaneStaffTypeFilter as WithStaffEnum[],
      facility: {
        id: selectedFacilityId,
        type: SessionService.assertUserType('B2B') ? 'Community' : 'AgencyCommunity',
      },
    };
  }

  if (selectedShiftStatusFilters?.length) {
    lineFilter.withStatus = selectedShiftStatusFilters;
  }

  if (selectedEmploymentStatusFilter?.length) {
    lineFilter.byEmploymentStatus = selectedEmploymentStatusFilter;
  }

  if (selectedTimeFrameStatusFilter?.length) {
    lineFilter.withTimeFrame = selectedTimeFrameStatusFilter;
  }

  if (selectedPositionCodeFilter?.length) {
    lineFilter.withPositionCode = selectedPositionCodeFilter.map((code: number) => String(code));
  }

  return {
    scheduleFilter: {
      withId: selectedSchedulesRelayIds,
      withCommunity: selectedFacilityId,
    },
    includeConflictsAndWarnings: IncludeConflictsAndWarningsEnum.CONFLICTS_AND_WARNINGS,
    lineFilter,
    lineStartDate: from,
    lineEndDate: to,
    ...(isShowPositionShiftsEnabled
      ? { aggregatePositionShifts: AggregatePositionShiftsEnum.ALL }
      : {}),
  };
};

export const useFetchScheduleLinesData = () => {
  const selectedFacilityId = useSelector(selectedScheduleFacilityIdSelector);
  const selectedSchedulesRelayIds = useSelector(selectedLineScheduleRelayIdsSelector);
  const selectedScheduleStartDate = useSelector(selectedScheduleStartDateSelector);
  const selectedScheduleEndDate = useSelector(selectedLineScheduleEndDateSelector);
  const isShowPositionShiftsEnabled = useSelector(isPositionShiftsEnableSelector);
  const selectedJaneStaffTypeFilter = useSelector(selectedJaneStaffTypeFilterSelector);
  const selectedShiftStatusFilters = useSelector(selectedShiftStatusFiltersSelector);
  const selectedEmploymentStatusFilter = useSelector(selectedEmploymentStatusFilterSelector);
  const selectedTimeFrameStatusFilter = useSelector(selectedTimeFrameStatusFilterSelector);
  const selectedPositionCodeFilter = useSelector(selectedPositionCodeFilterSelector);
  const isShowConflictsOnlyChecked = useSelector(isShowConflictsOnlyFilterSelectedSelector);
  const [hasRunInitialLoad, setHasRunInitialLoad] = useState(false);

  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const { fetchData } = useScheduleLinesStore();
  const apolloClient = useMemo(() => createApolloClient(), []);

  // Track previous values to detect changes
  const prevFiltersRef = useRef({
    selectedFacilityId,
    selectedSchedulesRelayIds,
    selectedScheduleStartDate,
    selectedScheduleEndDate,
    isShowPositionShiftsEnabled,
    selectedJaneStaffTypeFilter,
    selectedShiftStatusFilters,
    selectedEmploymentStatusFilter,
    selectedTimeFrameStatusFilter,
    selectedPositionCodeFilter,
    isShowConflictsOnlyChecked,
  });

  useEffect(() => {
    const currentFilters = {
      selectedFacilityId,
      selectedSchedulesRelayIds,
      selectedScheduleStartDate,
      selectedScheduleEndDate,
      isShowPositionShiftsEnabled,
      selectedJaneStaffTypeFilter,
      selectedShiftStatusFilters,
      selectedEmploymentStatusFilter,
      selectedTimeFrameStatusFilter,
      selectedPositionCodeFilter,
      isShowConflictsOnlyChecked,
    };

    // Check if any filter has changed
    const hasChanged = Object.keys(currentFilters).some(
      (key) =>
        JSON.stringify(prevFiltersRef.current[key as keyof typeof currentFilters]) !==
        JSON.stringify(currentFilters[key as keyof typeof currentFilters]),
    );

    const shouldTriggerFetchData =
      selectedFacilityId &&
      selectedSchedulesRelayIds.length > 0 &&
      (!hasRunInitialLoad || hasChanged) &&
      selectedSchedulesRelayIds?.length;

    if (shouldTriggerFetchData) {
      if (!hasRunInitialLoad) {
        setHasRunInitialLoad(true);
      }

      const timeZone = currentlySelectedLocation ? currentlySelectedLocation.timeZone : undefined;

      const lineStartDate = DateTime.fromISO(selectedScheduleStartDate)
        .setZone(timeZone, {
          keepLocalTime: true,
        })
        .startOf('day');
      const lineEndDate = DateTime.fromISO(selectedScheduleEndDate)
        .setZone(timeZone, {
          keepLocalTime: true,
        })
        .endOf('day');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let lineFilter: any = {};

      if (selectedJaneStaffTypeFilter?.length) {
        lineFilter.withStaff = {
          types: selectedJaneStaffTypeFilter as WithStaffEnum[],
          facility: {
            id: selectedFacilityId,
            type: SessionService.assertUserType('B2B') ? 'Community' : 'AgencyCommunity',
          },
        };
      }

      if (selectedShiftStatusFilters?.length) {
        lineFilter.withStatus = selectedShiftStatusFilters;
      }

      if (selectedEmploymentStatusFilter?.length) {
        lineFilter.byEmploymentStatus = selectedEmploymentStatusFilter;
      }

      if (selectedTimeFrameStatusFilter?.length) {
        lineFilter.withTimeFrame = selectedTimeFrameStatusFilter;
      }

      if (selectedPositionCodeFilter?.length) {
        lineFilter.withPositionCode = selectedPositionCodeFilter.map((code: number) =>
          String(code),
        );
      }

      fetchData(
        apolloClient,
        {
          scheduleFilter: {
            withId: selectedSchedulesRelayIds,
            withCommunity: selectedFacilityId,
          },
          lineFilter,
          includeConflictsAndWarnings: isShowConflictsOnlyChecked
            ? IncludeConflictsAndWarningsEnum.CONFLICTS_ONLY
            : IncludeConflictsAndWarningsEnum.CONFLICTS_AND_WARNINGS,
          lineStartDate,
          lineEndDate,
          ...(isShowPositionShiftsEnabled
            ? { aggregatePositionShifts: AggregatePositionShiftsEnum.ALL }
            : {}),
        },
        true,
      );
    }
    // Update previous values
    prevFiltersRef.current = currentFilters;
  }, [
    selectedFacilityId,
    selectedSchedulesRelayIds,
    fetchData,
    apolloClient,
    selectedScheduleStartDate,
    selectedScheduleEndDate,
    hasRunInitialLoad,
    isShowPositionShiftsEnabled,
    selectedJaneStaffTypeFilter,
    selectedShiftStatusFilters,
    selectedEmploymentStatusFilter,
    selectedTimeFrameStatusFilter,
    selectedPositionCodeFilter,
    isShowConflictsOnlyChecked,
    currentlySelectedLocation,
  ]);
};

export const useLineSchedulePageData = ({ facilityId }: { facilityId: number }) => {
  const { fetchData: fetchLineSchedules } = useScheduleLinesStore();
  const variables = useFetchScheduleLinesVariables();

  const { fetchData: fetchSchedules, hasInitialized: schedulesInitialized } = useSchedulesStore();

  const [localSchedulesInitialized, setLocalSchedulesInitialized] = useState(schedulesInitialized);

  const currentlySelectedLocation = getCurrentlySelectedLocation();

  const selectedScheduleStartDate = useSelector(selectedScheduleStartDateSelector);
  const selectedScheduleEndDate = useSelector(selectedLineScheduleEndDateSelector);

  const timeZone = currentlySelectedLocation ? currentlySelectedLocation.timeZone : undefined;

  const lineStartDate = DateTime.fromISO(selectedScheduleStartDate)
    .setZone(timeZone, { keepLocalTime: true })
    .startOf('day')
    .toString();
  const lineEndDate = DateTime.fromISO(selectedScheduleEndDate)
    .setZone(timeZone, {
      keepLocalTime: true,
    })
    .endOf('day')
    .toString();

  const apolloClient = useMemo(() => createApolloClient(), []);
  const scheduleVariables: ScheduleByLineSchedulesQueryVariables = useMemo(
    () => ({
      scheduleFilter: {
        withCommunity: facilityId,
      },
      lineStartDate,
      lineEndDate,
    }),
    [facilityId, lineEndDate, lineStartDate],
  );

  // Fetch schedules when facilityId is available
  useEffect(() => {
    if (!localSchedulesInitialized && facilityId) {
      fetchSchedules(apolloClient, scheduleVariables);
      setLocalSchedulesInitialized(true);
    }
  }, [apolloClient, facilityId, fetchSchedules, scheduleVariables, localSchedulesInitialized]);

  const refetchAllLocationStores = useCallback(async () => {
    if (facilityId) {
      await fetchSchedules(apolloClient, scheduleVariables);
      setLocalSchedulesInitialized(true);
      if (variables?.scheduleFilter?.withId?.length)
        await fetchLineSchedules(apolloClient, variables, true);
    }
  }, [facilityId, fetchSchedules, apolloClient, variables, fetchLineSchedules, scheduleVariables]);

  return {
    schedulesInitialized,
    isInitialized: schedulesInitialized,
    refetchAllLocationStores,
  };
};
